import { createContext, useContext, useState, type ReactNode } from 'react';

import { jwtDecode } from 'jwt-decode';

import { api } from '@/lib/api-client';

interface SessionInfo {
  sessionToken: string;
  username: string;
  /** Azure AD display name. Absent on sessions opened before it was kept. */
  displayName?: string;
}

interface SessionTokenResponse {
  session_token: string;
}

interface AuthState {
  isAuthenticated: boolean;
  username: string | null;
  displayName: string | null;
  isLoading: boolean;
  error: string | null;
  shouldRedirectToLogin: boolean;
}

interface AuthContextType extends AuthState {
  signin: (
    username: string,
    idToken: string,
    displayName?: string,
  ) => Promise<void>;
  signout: () => void;
  checkAuth: (showAlert?: boolean) => boolean;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_STORAGE_KEY = 'sessionInfo';

function isValidToken(token: string | undefined): boolean {
  if (!token) return false;

  try {
    const decoded = jwtDecode(token);
    const now = Date.now();

    if (decoded.exp) {
      return decoded.exp * 1000 > now;
    }

    return false;
  } catch (error) {
    console.error('Token validation failed:', error);
    return false;
  }
}

function getStoredSession(): SessionInfo | null {
  try {
    const session = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!session) return null;

    return JSON.parse(session) as SessionInfo;
  } catch (error) {
    console.error('Failed to parse stored session:', error);
    return null;
  }
}

function storeSession(session: SessionInfo): void {
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
}

function clearSession(): void {
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [authState, setAuthState] = useState<AuthState>(() => {
    const session = getStoredSession();
    if (session && isValidToken(session.sessionToken)) {
      return {
        isAuthenticated: true,
        username: session.username,
        displayName: session.displayName ?? null,
        isLoading: false,
        error: null,
        shouldRedirectToLogin: false,
      };
    }

    if (session) {
      clearSession();
    }

    return {
      isAuthenticated: false,
      username: null,
      displayName: null,
      isLoading: false,
      error: null,
      shouldRedirectToLogin: false,
    };
  });

  const checkAuth = (showAlert = false): boolean => {
    const session = getStoredSession();

    if (!session || !isValidToken(session.sessionToken)) {
      if (authState.isAuthenticated) {
        clearSession();

        if (showAlert) {
          alert(
            'Your session has expired. You will be redirected to the login screen.',
          );

          // Set flag to trigger redirect in NavigationGuard
          setAuthState({
            isAuthenticated: false,
            username: null,
            displayName: null,
            isLoading: false,
            error: 'Session expired',
            shouldRedirectToLogin: true,
          });
        } else {
          setAuthState({
            isAuthenticated: false,
            username: null,
            displayName: null,
            isLoading: false,
            error: 'Session expired',
            shouldRedirectToLogin: false,
          });
        }
      }
      return false;
    }

    return true;
  };

  const signin = async (
    username: string,
    idToken: string,
    displayName?: string,
  ): Promise<void> => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      const response = await api.get<SessionTokenResponse>(
        new URL('v1/login/azuread', import.meta.env.VITE_ADMIN_API_URL).href,
        {
          params: { access_token: idToken },
        },
      );

      const sessionToken = response.data.session_token;
      const session: SessionInfo = {
        sessionToken,
        username,
        displayName,
      };

      storeSession(session);

      setAuthState({
        isAuthenticated: true,
        username,
        displayName: displayName ?? null,
        isLoading: false,
        error: null,
        shouldRedirectToLogin: false,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Authentication failed';

      setAuthState({
        isAuthenticated: false,
        username: null,
        displayName: null,
        isLoading: false,
        error: errorMessage,
        shouldRedirectToLogin: false,
      });

      throw new Error(errorMessage);
    }
  };

  const signout = (): void => {
    clearSession();
    setAuthState({
      isAuthenticated: false,
      username: null,
      displayName: null,
      isLoading: false,
      error: null,
      shouldRedirectToLogin: false,
    });
  };

  const clearError = (): void => {
    setAuthState((prev) => ({ ...prev, error: null }));
  };

  const value: AuthContextType = {
    ...authState,
    signin,
    signout,
    checkAuth,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
